import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraHeading, SpecraText, specraButtonVariants } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/authContext';
import { formatContractDate } from '../../../lib/contractDate';
import { cn } from '../../../lib/utils';
import { contractsClient } from './Contracts/api';
import { safeReturnTo } from './returnTo';
/** Read-only connected contexts inside the existing bounded Contact drawer. */ export function ContactDetail({ contactId }) {
    const { user } = useAuth();
    const location = useLocation();
    const returnTo = safeReturnTo(location.pathname + location.search + location.hash);
    const work = useQuery({
        queryKey: [
            'contact-connected-work',
            user?.tenant_id,
            user?.id,
            contactId
        ],
        queryFn: ({ signal })=>contractsClient.listContactConnectedWork({
                contactId
            }, {
                signal
            }),
        enabled: !!user?.id && !!user.tenant_id && !!contactId,
        retry: false,
        staleTime: 0
    });
    return /*#__PURE__*/ _jsxs("section", {
        "data-testid": Testids.deliveryRegistries.contactConnectedWork,
        "aria-label": "Connected work",
        className: "space-y-3",
        children: [
            /*#__PURE__*/ _jsx(SpecraHeading, {
                size: "sm",
                children: "Connected work"
            }),
            work.isFetching ? /*#__PURE__*/ _jsx("output", {
                children: /*#__PURE__*/ _jsx(SpecraText, {
                    children: "Loading connected work…"
                })
            }) : work.isError ? /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraText, {
                        role: "alert",
                        children: "Couldn’t load connected work."
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "outline",
                        size: "sm",
                        onClick: ()=>void work.refetch(),
                        children: "Retry connected work"
                    })
                ]
            }) : !work.data?.items.length ? /*#__PURE__*/ _jsx("output", {
                children: /*#__PURE__*/ _jsx(SpecraText, {
                    children: "No connected work available to show."
                })
            }) : /*#__PURE__*/ _jsx("ul", {
                className: "space-y-3",
                children: work.data.items.map((item)=>{
                    const owner = /^\/(capture|delivery\/programs)\/[^/?#]+$/.test(item.ownerPath) ? safeReturnTo(item.ownerPath) : null;
                    return /*#__PURE__*/ _jsxs("li", {
                        className: "flex min-w-0 flex-col items-start gap-2 rounded-md border border-border p-3",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraText, {
                                className: "font-medium",
                                children: item.contextName
                            }),
                            /*#__PURE__*/ _jsx(SpecraText, {
                                tone: "muted",
                                children: item.type
                            }),
                            /*#__PURE__*/ _jsx(SpecraText, {
                                children: item.role || 'Relationship role unknown'
                            }),
                            /*#__PURE__*/ _jsx(SpecraText, {
                                children: item.occurredAt ? formatContractDate(item.occurredAt) : 'Date unknown'
                            }),
                            /*#__PURE__*/ _jsx(SpecraText, {
                                tone: "muted",
                                children: item.historicalOrganization || 'Historical organization unknown'
                            }),
                            owner && returnTo && /*#__PURE__*/ _jsxs(Link, {
                                className: cn(specraButtonVariants({
                                    variant: 'outline',
                                    size: 'sm'
                                }), 'h-auto max-w-full whitespace-normal break-words py-2 text-left'),
                                to: `${owner}?${new URLSearchParams({
                                    returnTo
                                })}`,
                                children: [
                                    "Open ",
                                    item.contextName
                                ]
                            })
                        ]
                    }, `${item.type}:${item.id}`);
                })
            })
        ]
    });
}
